import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  X,
  Bell,
  ClipboardCheck,
  RefreshCw,
  Clock,
  Calendar,
  CalendarPlus,
  CalendarClock,
  CalendarCheck,
  AlertCircle,
  AlertTriangle,
  Sparkles,
  AtSign,
} from "lucide-react"
import { getNotifications, markAsRead, markAllAsRead } from "services/notifications"

// Interfaccia per le notifiche dall'API
interface Notification {
  id: number
  type: string
  typeKey: string
  name: string
  description: string
  time: string
  isRead: boolean
  link?: string | null
  timestamp?: Date
  // Campi API backend
  signal_function_name?: string
  message?: string
  is_read?: boolean
  created_at?: string
}

interface NotificationsModalProps {
  isOpen: boolean
  onClose: () => void
  onUnreadCountChange?: (count: number) => void
}

// Helper per formattare il tempo relativo
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 1) return "Ora"
  if (diffMins < 60) return `${diffMins} min fa`
  if (diffHours < 24) return `${diffHours} ore fa`
  if (diffDays === 1) return "Ieri"
  if (diffDays < 7) return `${diffDays} giorni fa`
  return date.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })
}

// Mappa i link generati dal backend (es. "/tasks/3/") alle route frontend
// realmente esistenti in App.js. Le route attuali sono piatte: non esistono
// dettagli /tasks/:id, quindi si rinvia alla pagina lista corrispondente.
function mapNotificationLink(backendLink: string | null | undefined): string | null {
  if (!backendLink) return null
  if (backendLink.startsWith("/tasks/")) return "/tasks"
  if (backendLink.startsWith("/appointments/")) return "/agenda"
  if (backendLink.startsWith("/contacts/")) return "/contacts"
  if (backendLink.startsWith("/leads/")) return "/leads"
  return null
}

// Helper per mappare il tipo di notifica.
// Le chiavi corrispondono ai signal_function_name impostati lato backend
// (api_CRM/notifications/signals.py e api_CRM/notifications/tasks.py).
function getNotificationType(signalFunctionName?: string): string {
  const typeMap: Record<string, string> = {
    // Signal Django: nuovo task assegnato (m2m_changed)
    task_assigned: "Nuovo Task",
    // Signal Django: task modificato (post_save)
    task_updated: "Aggiornamento",
    // Legacy: notifiche storiche pre-refactor — trattate come "Nuovo Task Assegnato"
    create_task_notification: "Nuovo Task",
    // Celery beat: reminder scadenza task (legacy single-scenario)
    create_task_due_reminders: "Scadenza",
    // Celery beat: reminder appuntamento (legacy single-scenario)
    create_appointment_reminders: "Appuntamento",
    // Signal Django: evento creato / partecipante aggiunto (m2m_changed)
    event_created: "Evento",
    // Signal Django: evento modificato (post_save)
    event_updated: "Evento aggiornato",
    // Celery beat: reminder task multi-scenario
    task_reminder_upcoming: "Scadenza",
    task_reminder_due: "Scadenza",
    task_reminder_overdue: "Scaduto",
    // Celery beat: reminder evento multi-scenario
    event_reminder_upcoming: "Promemoria",
    event_reminder_due: "Promemoria",
    event_reminder_1h: "Tra poco",
    mention: "Menzione",
    ai_agent: "Agent AI",
  }
  return typeMap[signalFunctionName || ""] || "Notifica"
}

// Icona per tipo di notifica (basata sul signal_function_name raw)
function getNotificationIcon(typeKey: string) {
  const className = "w-4 h-4"
  switch (typeKey) {
    case "task_assigned":
    case "create_task_notification": // legacy
      return <ClipboardCheck className={className} />
    case "task_updated":
      return <RefreshCw className={className} />
    case "create_task_due_reminders":
      return <Clock className={className} />
    case "create_appointment_reminders":
      return <Calendar className={className} />
    // Eventi (signal m2m_changed / post_save su Appointment)
    case "event_created":
      return <CalendarPlus className={className} />
    case "event_updated":
      return <CalendarClock className={className} />
    // Reminder task multi-scenario
    case "task_reminder_upcoming":
      return <Clock className={className} />
    case "task_reminder_due":
      return <AlertCircle className={className} />
    case "task_reminder_overdue":
      return <AlertTriangle className={className} />
    // Reminder evento multi-scenario
    case "event_reminder_upcoming":
      return <CalendarCheck className={className} />
    case "event_reminder_due":
      return <Calendar className={className} />
    case "event_reminder_1h":
      return <AlertCircle className={className} />
    case "mention":
      return <AtSign className={className} />
    case "ai_agent":
      return <Sparkles className={className} />
    default:
      return <Bell className={className} />
  }
}

// Colori semantici per tipo (fissi tra temi)
function getNotificationColor(typeKey: string): string {
  switch (typeKey) {
    case "task_assigned":
    case "create_task_notification":
      return "text-[#7b3fff]"
    case "task_updated":
      return "text-[#3b82f6]"
    case "create_task_due_reminders":
      return "text-[#f59e0b]"
    case "create_appointment_reminders":
      return "text-[#10b981]"
    case "event_created":
      return "text-[#10b981]"
    case "event_updated":
      return "text-[#3b82f6]"
    case "task_reminder_upcoming":
      return "text-[#f59e0b]"
    case "task_reminder_due":
      return "text-[#f59e0b]"
    case "task_reminder_overdue":
      return "text-[#ef4444]"
    case "event_reminder_upcoming":
      return "text-[#10b981]"
    case "event_reminder_due":
      return "text-[#10b981]"
    case "event_reminder_1h":
      return "text-[#f59e0b]"
    case "mention":
      return "text-[#ec4899]"
    case "ai_agent":
      return "text-[#8b5cf6]"
    default:
      return "text-[var(--hexa-purple)]"
  }
}

function getNotificationBg(typeKey: string): string {
  switch (typeKey) {
    case "task_assigned":
    case "create_task_notification":
      return "bg-[#7b3fff]/15"
    case "task_updated":
      return "bg-[#3b82f6]/15"
    case "create_task_due_reminders":
      return "bg-[#f59e0b]/15"
    case "create_appointment_reminders":
      return "bg-[#10b981]/15"
    case "event_created":
      return "bg-[#10b981]/15"
    case "event_updated":
      return "bg-[#3b82f6]/15"
    case "task_reminder_upcoming":
      return "bg-[#f59e0b]/15"
    case "task_reminder_due":
      return "bg-[#f59e0b]/15"
    case "task_reminder_overdue":
      return "bg-[#ef4444]/15"
    case "event_reminder_upcoming":
      return "bg-[#10b981]/15"
    case "event_reminder_due":
      return "bg-[#10b981]/15"
    case "event_reminder_1h":
      return "bg-[#f59e0b]/15"
    case "mention":
      return "bg-[#ec4899]/15"
    case "ai_agent":
      return "bg-[#8b5cf6]/15"
    default:
      return "bg-[var(--hexa-purple)]/15"
  }
}

// Raggruppa le notifiche in Oggi / Ieri / Precedenti
function groupNotifications(notifications: Notification[]) {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86400000)

  const groups = [
    { key: "oggi", label: "Oggi", notifications: [] as Notification[] },
    { key: "ieri", label: "Ieri", notifications: [] as Notification[] },
    { key: "precedenti", label: "Precedenti", notifications: [] as Notification[] },
  ]

  notifications.forEach((n) => {
    const date = n.timestamp ? new Date(n.timestamp) : null
    if (!date) {
      groups[2].notifications.push(n)
    } else if (date >= today) {
      groups[0].notifications.push(n)
    } else if (date >= yesterday) {
      groups[1].notifications.push(n)
    } else {
      groups[2].notifications.push(n)
    }
  })

  return groups
}

export function NotificationsModal({
  isOpen,
  onClose,
  onUnreadCountChange,
}: NotificationsModalProps) {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [nextUrl, setNextUrl] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadNotifications()
    }
  }, [isOpen])

  // Mappa una notifica dal formato API al formato del componente
  const formatNotification = (n: any): Notification => ({
    id: n.id,
    type: getNotificationType(n.signal_function_name),
    typeKey: n.signal_function_name || "",
    name: n.name || n.title || "",
    description: n.message || n.description || "",
    time: n.created_at ? formatRelativeTime(n.created_at) : "",
    isRead: n.is_read ?? false,
    link: n.link ?? null,
    timestamp: n.created_at ? new Date(n.created_at) : undefined,
  })

  const loadNotifications = async () => {
    setLoading(true)
    try {
      const { results, next } = await getNotifications()

      const formattedNotifications: Notification[] = results.map(formatNotification)

      // Ordina per timestamp (più recenti prima)
      formattedNotifications.sort((a, b) => {
        if (!a.timestamp || !b.timestamp) return 0
        return b.timestamp.getTime() - a.timestamp.getTime()
      })

      setNotifications(formattedNotifications)
      setNextUrl(next)

      // Update unread count
      const unreadCount = formattedNotifications.filter((n) => !n.isRead).length
      if (onUnreadCountChange) {
        onUnreadCountChange(unreadCount)
      }
    } catch (error) {
      console.error("Error loading notifications:", error)
      setNotifications([])
      setNextUrl(null)
      if (onUnreadCountChange) {
        onUnreadCountChange(0)
      }
    } finally {
      setLoading(false)
    }
  }

  const loadMoreNotifications = async () => {
    if (!nextUrl || loadingMore) return
    setLoadingMore(true)
    try {
      const { results, next } = await getNotifications(nextUrl)
      const newFormatted: Notification[] = results.map(formatNotification)
      setNotifications((prev) => {
        // Evita duplicati per id (paranoid check)
        const existingIds = new Set(prev.map((n) => n.id))
        const merged = [...prev, ...newFormatted.filter((n) => !existingIds.has(n.id))]
        merged.sort((a, b) => {
          if (!a.timestamp || !b.timestamp) return 0
          return b.timestamp.getTime() - a.timestamp.getTime()
        })
        return merged
      })
      setNextUrl(next)
    } catch (error) {
      console.error("Error loading more notifications:", error)
    } finally {
      setLoadingMore(false)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead()
      const updatedNotifications = notifications.map((n) => ({ ...n, isRead: true }))
      setNotifications(updatedNotifications)

      if (onUnreadCountChange) {
        onUnreadCountChange(0)
      }
    } catch (error) {
      console.error("Error marking all as read:", error)
    }
  }

  const handleMarkAsRead = async (id: number) => {
    try {
      await markAsRead(id)
      const updatedNotifications = notifications.map((n) =>
        n.id === id ? { ...n, isRead: true } : n
      )
      setNotifications(updatedNotifications)

      const unreadCount = updatedNotifications.filter((n) => !n.isRead).length
      if (onUnreadCountChange) {
        onUnreadCountChange(unreadCount)
      }
    } catch (error) {
      console.error("Error marking as read:", error)
    }
  }

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read se non letta
    if (!notification.isRead) {
      try {
        await handleMarkAsRead(notification.id)
      } catch (error) {
        console.error("Error marking as read:", error)
      }
    }

    // Naviga al link se presente, usando la mappatura sulle route reali
    const targetRoute = mapNotificationLink(notification.link)
    if (targetRoute) {
      // Estrai l'ID numerico dal link backend (es. "/tasks/3/" → 3)
      const idMatch = notification.link?.match(/\/(\d+)\/?$/)
      const entityId = idMatch ? parseInt(idMatch[1], 10) : null

      onClose()
      if (targetRoute === "/tasks" && entityId !== null) {
        navigate(targetRoute, { state: { openDetailId: entityId } })
      } else if (targetRoute === "/agenda" && entityId !== null) {
        navigate(targetRoute, { state: { highlightEventId: entityId } })
      } else {
        navigate(targetRoute)
      }
    }
  }

  if (!isOpen) return null

  const unreadCount = notifications.filter((n) => !n.isRead).length
  const hasUnread = unreadCount > 0
  const groupedNotifications = groupNotifications(notifications)

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-[var(--hexa-overlay)] z-[100] animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed top-12 right-6 z-[101] w-full max-w-[380px] animate-in slide-in-from-top duration-200">
        <div className="bg-[var(--hexa-modal-bg)] rounded-lg shadow-2xl flex flex-col max-h-[calc(100vh-4rem)]">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-[var(--hexa-border)] flex-shrink-0">
            <div className="flex items-baseline gap-1.5">
              <h2 className="text-[var(--hexa-text-primary)] text-[13px] font-medium">Notifiche</h2>
              {unreadCount > 0 && (
                <span className="text-[var(--hexa-text-muted)] text-[11px]">· {unreadCount}</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {hasUnread && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-[var(--hexa-text-muted)] hover:text-[var(--hexa-purple)] text-[11px] transition-colors"
                >
                  Segna tutte come lette
                </button>
              )}
              <button
                onClick={onClose}
                className="text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto scrollbar-hexa px-4 py-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <span className="text-[var(--hexa-text-secondary)] text-[12px]">Caricamento...</span>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-10 h-10 rounded-xl bg-[var(--hexa-surface-hover)] flex items-center justify-center mb-3">
                  <Bell className="w-5 h-5 text-[var(--hexa-text-muted)]" />
                </div>
                <p className="text-[var(--hexa-text-secondary)] text-[12px] font-medium mb-1">
                  Nessuna notifica
                </p>
                <p className="text-[var(--hexa-text-muted)] text-[10px]">
                  Quando arriverà qualcosa lo vedrai qui
                </p>
              </div>
            ) : (
              <>
                {groupedNotifications.map(
                (group) =>
                  group.notifications.length > 0 && (
                    <div key={group.key} className="mb-4 last:mb-0">
                      <div className="text-[10px] font-semibold text-[var(--hexa-text-muted)] uppercase tracking-wider mb-2 px-1">
                        {group.label}
                      </div>
                      <div className="space-y-1.5">
                        {group.notifications.map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification)}
                            className={`relative rounded-lg p-3 transition-all duration-200 cursor-pointer border ${
                              notification.isRead
                                ? "border-transparent hover:bg-[var(--hexa-surface-hover)] hover:border-[var(--hexa-border)]"
                                : "bg-[var(--hexa-purple)]/[0.08] hover:bg-[var(--hexa-purple)]/[0.14] border-[var(--hexa-purple)]/20"
                            }`}
                          >
                            <div className="flex gap-3">
                              {/* Icona tipo */}
                              <div
                                className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                  notification.isRead
                                    ? "bg-[var(--hexa-surface-hover)] text-[var(--hexa-text-muted)] opacity-50"
                                    : `${getNotificationBg(notification.typeKey)} ${getNotificationColor(notification.typeKey)}`
                                }`}
                              >
                                {getNotificationIcon(notification.typeKey)}
                              </div>

                              {/* Contenuto */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-0.5">
                                  <h3
                                    className={`text-[11px] font-medium leading-tight line-clamp-1 ${
                                      notification.isRead
                                        ? "text-[var(--hexa-text-muted)]"
                                        : "text-[var(--hexa-text-primary)]"
                                    }`}
                                  >
                                    {notification.name}
                                  </h3>
                                  {!notification.isRead && (
                                    <div className="w-2 h-2 bg-[var(--hexa-purple)] rounded-full flex-shrink-0 mt-1" />
                                  )}
                                </div>

                                <p
                                  className={`text-[10px] leading-relaxed line-clamp-2 ${
                                    notification.isRead
                                      ? "text-[var(--hexa-text-muted)] opacity-60"
                                      : "text-[var(--hexa-text-secondary)]"
                                  }`}
                                >
                                  {notification.description}
                                </p>

                                <div className="flex items-center gap-2 mt-1.5">
                                  <span
                                    className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${
                                      notification.isRead
                                        ? "bg-[var(--hexa-surface-hover)] text-[var(--hexa-text-muted)] opacity-60"
                                        : `${getNotificationBg(notification.typeKey)} ${getNotificationColor(notification.typeKey)}`
                                    }`}
                                  >
                                    {notification.type}
                                  </span>
                                  <span className="text-[var(--hexa-text-muted)] text-[10px]">
                                    {notification.time}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                )}
                {nextUrl && (
                  <div className="pt-2 pb-1">
                    <button
                      onClick={loadMoreNotifications}
                      disabled={loadingMore}
                      className="w-full py-2 rounded-lg text-[11px] font-medium text-[var(--hexa-text-muted)] hover:text-[var(--hexa-purple)] hover:bg-[var(--hexa-surface-hover)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loadingMore ? "Caricamento..." : "Mostra altre"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
